'use client';

import React, { useEffect, useRef, useState } from 'react';
import ContactModal from './ContactModal';

export default function HeroSection() {
  const parallaxRef = useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (!parallaxRef?.current) return;
      const y = window.scrollY;
      parallaxRef.current.style.transform = `translateY(${y * 0.35}px)`;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section
      className="relative flex flex-col overflow-hidden"
      aria-label="Зеркало в прошлое — оживите воспоминания молодости">

      {/* ── MOBILE layout: image on top, content below ── */}
      <div className="block lg:hidden">
        {/* Image fully visible on mobile */}
        <div className="w-full relative">
          <img
            src="/assets/images/8f30c8a6-2144-4934-b6a2-4679173369fe-1779606835338.png"
            alt="Портрет молодой женщины в ретро-стиле, тёплые тона"
            className="w-full h-auto object-contain"
          />
          {/* subtle gradient at bottom of image */}
          <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-b from-transparent to-plum/80" />
        </div>

        {/* Content below image on mobile */}
        <div className="bg-plum px-6 py-10 text-center">
          <div className="flex items-center justify-center gap-4 mb-6">
            <span className="h-px w-10 bg-gold/60" />
            <span className="text-xs font-medium uppercase tracking-[0.25em] text-heather-light">
              Зеркало в прошлое
            </span>
            <span className="h-px w-10 bg-gold/60" />
          </div>

          <h1 className="display-serif text-parchment mb-6" style={{ fontSize: 'clamp(1.75rem, 4vw, 3.75rem)', lineHeight: '1.0', letterSpacing: '-0.02em' }}>
            Оживите свои
            <br />
            <em className="italic text-parchment/90">воспоминания</em>
          </h1>

          <div className="flex justify-center mb-6">
            <span className="gold-rule" aria-hidden="true" />
          </div>

          <p className="font-serif italic text-heather-light text-subhead max-w-xl mx-auto mb-8">
            <em>
              Создаем частичку ваших лучших воспоминаний, вдыхаем жизнь и музыку из прошлого для незабываемых мгновений настоящего. А ещё можем добавить немного магии и преобразить вас и ваши фото для памятных мероприятий.
            </em>
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
            <button
              onClick={() => setIsModalOpen(true)}
              className="btn-gold px-8 py-4 rounded-full text-sm font-semibold tracking-wide">
              Обратиться к мемористу
            </button>
            <a
              href="#story"
              className="text-sm font-medium text-parchment/75 hover:text-parchment transition-colors flex items-center gap-2">
              <span>Узнать нашу историю</span>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M8 3v10M3 8l5 5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </div>
        </div>
      </div>

      {/* ── DESKTOP layout: full-screen parallax background ── */}
      <div className="hidden lg:flex relative min-h-screen flex-col justify-end">
        {/* Background photo with parallax */}
        <div className="absolute inset-0 z-0 overflow-hidden" ref={parallaxRef}>
          <img
            src="/assets/images/8f30c8a6-2144-4934-b6a2-4679173369fe-1779606835338.png"
            alt="Портрет молодой женщины в ретро-стиле, тёплые тона"
            className="w-full h-full object-cover object-center scale-110"
          />
        </div>

        {/* Multi-layer gradient overlay */}
        <div className="absolute inset-0 z-10 bg-hero-overlay" />
        <div className="absolute inset-0 z-10 bg-gradient-to-b from-plum/60 via-transparent to-transparent" />
        <div
          className="absolute inset-0 z-10 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E")`,
            backgroundSize: '200px 200px'
          }} />

        {/* Hero content */}
        <div className="relative z-20 max-w-6xl mx-auto px-6 pb-20 lg:pb-32 w-full">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-4 mb-8 opacity-0 animate-fade-in"
              style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}>
              <span className="h-px w-10 bg-gold/60" />
              <span className="text-xs font-medium uppercase tracking-[0.25em] text-heather-light">
                Зеркало в прошлое
              </span>
              <span className="h-px w-10 bg-gold/60" />
            </div>

            <h1
              className="display-serif text-hero text-parchment mb-6 opacity-0 animate-fade-up"
              style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}>
              Оживите свои
              <br />
              <em className="italic text-parchment/90">воспоминания</em>
            </h1>

            <div className="flex justify-center mb-7">
              <span
                className="gold-rule gold-rule-animate"
                style={{ animationDelay: '0.9s' }}
                aria-hidden="true" />
            </div>

            <p
              className="font-serif italic text-heather-light text-subhead max-w-xl mx-auto opacity-0 animate-fade-in"
              style={{ animationDelay: '1.1s', animationFillMode: 'forwards' }}>
              <em>
                Создаем частичку ваших лучших воспоминаний, вдыхаем жизнь и музыку из прошлого для незабываемых мгновений настоящего. А ещё можем добавить немного магии и преобразить вас и ваши фото для памятных мероприятий.
              </em>
            </p>
          </div>

          <div
            className="flex flex-col sm:flex-row items-center justify-center gap-5 mt-10 opacity-0 animate-fade-in"
            style={{ animationDelay: '1.4s', animationFillMode: 'forwards' }}>
            <button
              onClick={() => setIsModalOpen(true)}
              className="btn-gold px-8 py-4 rounded-full text-sm font-semibold tracking-wide">
              Обратиться к мемористу
            </button>
            <a
              href="#story"
              className="text-sm font-medium text-parchment/75 hover:text-parchment transition-colors flex items-center gap-2">
              <span>Узнать нашу историю</span>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M8 3v10M3 8l5 5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 opacity-40">
          <span className="text-xs uppercase tracking-[0.2em] text-parchment/60">Листать</span>
          <div className="w-px h-10 bg-gradient-to-b from-gold/60 to-transparent animate-gold-pulse" />
        </div>
      </div>

      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </section>
  );
}
